import React from 'react';
import styled from '@emotion/styled';
import type { FlexProps, SizeProps, SpaceProps } from 'styled-system';
import { color, size, space } from 'styled-system';

import homeClubLogo from 'common/assets/icons/homeclub/logo.svg';
import { Flex } from 'common/components/Flex';
import type { CheckboxFieldProps } from 'common/components/Form/CheckboxField';
import CheckboxField from 'common/components/Form/CheckboxField';
import Separator from 'common/components/Separator';
import { BodyRegularBR2, HeadlineH9 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { ListStyleProps } from 'common/theme/system-utilities';
import { listStyle } from 'common/theme/system-utilities';
import type { FieldName } from '../enums';

type CommonCheckboxProps = Pick<
  CheckboxFieldProps<FieldName>,
  'name' | 'value' | 'onChange' | 'eventName'
>;

type HomeClubConsentCheckboxProps = CommonCheckboxProps;

const HOME_CLUB_LOGO_SIZE = 56;

const Icon = styled.img<SizeProps>`
  ${size};
`;

const BulletList = styled.ul<FlexProps & SpaceProps & ListStyleProps>`
  ${color};
  ${space};
  ${listStyle};
`;

export const HomeClubConsentCheckbox = ({
  name,
  value,
  onChange,
  eventName,
}: HomeClubConsentCheckboxProps) => {
  const { t } = useLocalization();

  const checkboxLabel = (
    <BodyRegularBR2
      as="span"
      color="sys.neutral.text.strong"
      dangerouslySetInnerHTML={{ __html: t('h24_hc_registration_consent_checkbox_label') }}
    />
  );

  return (
    <Flex
      p="sp_16"
      rowGap="sp_16"
      borderWidth="small"
      borderStyle="solid"
      borderRadius="medium"
      flexDirection="column"
      borderColor="sys.neutral.border.inactive"
    >
      <Flex flexDirection="column" rowGap="sp_8">
        <Flex alignItems="center" columnGap="sp_4">
          <Icon alt="" src={homeClubLogo} size={HOME_CLUB_LOGO_SIZE} />
          <HeadlineH9 color="sys.secondary.text.default" m="sp_0">
            {t('h24_hc_registration_consent_header')}
          </HeadlineH9>
        </Flex>

        <BulletList color="sys.neutral.text.default" m="sp_0" pl="sp_16" listStyle="disc">
          <BodyRegularBR2
            as="li"
            dangerouslySetInnerHTML={{
              __html: t('h24_hc_registration_consent_benefit_offers'),
            }}
          />

          <BodyRegularBR2
            as="li"
            dangerouslySetInnerHTML={{
              __html: t('h24_hc_registration_consent_benefit_pre_access'),
            }}
          />

          <BodyRegularBR2
            as="li"
            dangerouslySetInnerHTML={{
              __html: t('h24_hc_registration_consent_benefit_welcome_discount'),
            }}
          />
        </BulletList>
      </Flex>

      <Separator />

      <CheckboxField
        name={name}
        value={value}
        onChange={onChange}
        eventName={eventName}
        label={checkboxLabel}
      />
    </Flex>
  );
};
